/**
 * Resolve viewer tickets (plataforma → UDV).
 * Contrato: docs/CONTRATO-VIEWER-TICKETS.md, docs/CONTRATO-UDV-ELEVATION.md
 */

import {
  parsePlanQueryParam,
  parseTicketFromSearchParams,
  parseTicketPurposeParam,
} from '../plan/parsePlanQuery'
import { normalizeTicketProbe } from '../utils/pickViewerCandidate'
import { parseThemeQueryParam } from '../plan/chromeTheme'
import { TICKET_PURPOSE, UDV_PLANS } from '../plan/constants'

export const DEFAULT_TICKET_API = 'https://plataforma.tanjousoft.com.ar'

/**
 * Base Ticket API: query `ticketApi` (multi-portal) → env → default.
 * @param {URLSearchParams|null|undefined} [params]
 * @returns {string} base URL sin slash final
 */
export function getTicketApiBase(params) {
  try {
    const search =
      params ||
      (typeof window !== 'undefined' && window.location
        ? new URLSearchParams(window.location.search)
        : null)
    if (search) {
      const fromQuery = String(search.get('ticketApi') || '').trim()
      if (fromQuery) {
        // Solo http(s) absoluto para evitar open-redirect / SSRF vía path relativo
        if (/^https?:\/\//i.test(fromQuery)) {
          return fromQuery.replace(/\/+$/, '')
        }
      }
    }
  } catch (e) {
    /* ignore */
  }
  try {
    if (
      typeof process !== 'undefined' &&
      process.env &&
      process.env.REACT_APP_TICKET_API
    ) {
      const raw = String(process.env.REACT_APP_TICKET_API).trim()
      if (raw) return raw.replace(/\/+$/, '')
    }
  } catch (e) {
    /* ignore */
  }
  return DEFAULT_TICKET_API
}

/**
 * @param {URLSearchParams|null|undefined} params
 * @returns {string|null}
 */
export function parseTicketIdFromUrl(params) {
  return parseTicketFromSearchParams(params)
}

/**
 * Path style /v/{ticketId} (opcional).
 * @param {string} [pathname]
 * @returns {string|null}
 */
export function parseTicketIdFromPath(pathname) {
  if (!pathname || typeof pathname !== 'string') return null
  const m = pathname.match(/\/v\/([A-Za-z0-9_-]+)/)
  return m && m[1] ? m[1] : null
}

/**
 * @param {unknown} item
 * @returns {{
 *   ticketId: string,
 *   viewerUrl: string|null,
 *   studyInstanceUid: string|null,
 *   studyDate: string|null,
 *   studyTime: string|null,
 *   modality: string|null,
 *   description: string|null,
 *   patientName: string|null,
 *   isCurrent: boolean,
 *   tenantId: string|null,
 *   clinicName: string|null,
 *   edgeId: string|null,
 * }|null}
 */
export function normalizeRelatedStudyItem(item) {
  if (!item || typeof item !== 'object') return null
  const ticketId = String(
    item.ticket_id != null
      ? item.ticket_id
      : item.ticketId != null
        ? item.ticketId
        : ''
  ).trim()
  if (!ticketId) return null
  const str = (a, b) => {
    const v = a != null ? a : b
    if (v == null) return null
    const s = String(v).trim()
    return s !== '' ? s : null
  }
  const isCurrentRaw =
    item.is_current != null ? item.is_current : item.isCurrent
  const num = (a, b) => {
    const v = a != null ? a : b
    if (v == null || v === '') return null
    const n = Number(v)
    return Number.isFinite(n) && n >= 0 ? n : null
  }
  const seriesCount = num(item.series_count, item.seriesCount)
  const instanceCount = num(item.instance_count, item.instanceCount)
  let seriesImageCounts = null
  const rawCounts =
    item.series_image_counts != null
      ? item.series_image_counts
      : item.seriesImageCounts
  if (Array.isArray(rawCounts) && rawCounts.length) {
    seriesImageCounts = rawCounts
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n >= 0)
  }
  return {
    ticketId,
    viewerUrl: str(item.viewer_url, item.viewerUrl),
    studyInstanceUid: str(
      item.study_instance_uid,
      item.studyInstanceUid
    ),
    studyDate: str(item.study_date, item.studyDate),
    studyTime: str(item.study_time, item.studyTime),
    modality: str(item.modality, null),
    description: str(item.description, item.studyDescription),
    patientName: str(item.patient_name, item.patientName),
    isCurrent: Boolean(isCurrentRaw),
    tenantId: str(item.tenant_id, item.tenantId),
    clinicName: str(item.clinic_name, item.clinicName),
    edgeId: str(item.edge_id, item.edgeId),
    seriesCount,
    instanceCount,
    seriesImageCounts,
  }
}

/**
 * @param {unknown} raw
 * @returns {ReturnType<typeof normalizeRelatedStudyItem>[]}
 */
export function normalizeRelatedStudies(raw) {
  if (!Array.isArray(raw)) return []
  const out = []
  for (let i = 0; i < raw.length; i++) {
    const n = normalizeRelatedStudyItem(raw[i])
    if (n) out.push(n)
  }
  return out
}

/**
 * Ítem de worklist: related_studies + patient_id + order.
 * @param {unknown} item
 * @returns {(ReturnType<typeof normalizeRelatedStudyItem> & {
 *   patientId: string|null,
 *   order: number|null,
 * })|null}
 */
export function normalizeWorklistItem(item) {
  const base = normalizeRelatedStudyItem(item)
  if (!base) return null
  if (!item || typeof item !== 'object') return null
  const str = (a, b) => {
    const v = a != null ? a : b
    if (v == null) return null
    const s = String(v).trim()
    return s !== '' ? s : null
  }
  const orderRaw = item.order != null ? Number(item.order) : null
  const order =
    orderRaw != null && Number.isFinite(orderRaw) ? orderRaw : null
  return {
    ...base,
    patientId: str(item.patient_id, item.patientId),
    order,
  }
}

/**
 * @param {unknown} raw
 * @returns {ReturnType<typeof normalizeWorklistItem>[]}
 */
export function normalizeWorklistItems(raw) {
  if (!Array.isArray(raw)) return []
  const out = []
  for (let i = 0; i < raw.length; i++) {
    const n = normalizeWorklistItem(raw[i])
    if (n) out.push(n)
  }
  out.sort((a, b) => {
    const ao = a.order != null ? a.order : 9999
    const bo = b.order != null ? b.order : 9999
    return ao - bo
  })
  return out
}

/**
 * @param {object} raw
 * @returns {{
 *   kind: string,
 *   worklistId: string|null,
 *   title: string|null,
 *   expiresAt: string|null,
 *   offlineCache: boolean,
 *   prefetch: boolean,
 *   profile: string|null,
 *   shell: string|null,
 *   items: ReturnType<typeof normalizeWorklistItems>,
 *   raw: object,
 * }}
 */
export function normalizeWorklistResponse(raw) {
  const data = raw && typeof raw === 'object' ? raw : {}
  const worklistIdRaw =
    data.worklist_id != null
      ? data.worklist_id
      : data.worklistId != null
        ? data.worklistId
        : null
  const worklistId =
    worklistIdRaw != null && String(worklistIdRaw).trim() !== ''
      ? String(worklistIdRaw).trim()
      : null
  const titleRaw = data.title != null ? String(data.title).trim() : ''
  const itemsRaw = data.items != null ? data.items : data.studies
  const items = normalizeWorklistItems(itemsRaw)
  const profile =
    parsePlanQueryParam(data.profile) || parsePlanQueryParam(data.plan) || null
  const shell =
    data.shell != null && String(data.shell).trim() !== ''
      ? String(data.shell).trim()
      : null
  return {
    kind:
      data.kind != null && String(data.kind).trim() !== ''
        ? String(data.kind).trim()
        : 'worklist',
    worklistId,
    title: titleRaw !== '' ? titleRaw : null,
    expiresAt: data.expires_at || data.expiresAt || null,
    offlineCache: parseBooleanPolicyField(
      data.offline_cache != null ? data.offline_cache : data.offlineCache
    ),
    prefetch: Boolean(
      data.prefetch === true ||
        data.prefetch === 1 ||
        data.prefetch === '1' ||
        data.prefetch === 'true'
    ),
    profile,
    shell,
    items,
    raw: data,
  }
}

/**
 * Ítem a abrir al boot: is_current, luego menor order, luego primero.
 * @param {ReturnType<typeof normalizeWorklistItems>} items
 * @returns {ReturnType<typeof normalizeWorklistItem>|null}
 */
export function pickWorklistCurrentItem(items) {
  const list = Array.isArray(items) ? items : []
  if (!list.length) return null
  const marked = list.find((it) => it && it.isCurrent)
  if (marked) return marked
  return list[0]
}

/**
 * @param {string} worklistId
 * @param {{ baseUrl?: string, fetchImpl?: typeof fetch }} [opts]
 * @returns {Promise<{
 *   ok: boolean,
 *   status?: number,
 *   worklist?: ReturnType<typeof normalizeWorklistResponse>,
 *   reason?: string,
 * }>}
 */
export async function resolveViewerWorklist(worklistId, opts = {}) {
  const id = worklistId != null ? String(worklistId).trim() : ''
  if (!id) {
    return { ok: false, reason: 'missing_worklist' }
  }
  const base = (opts.baseUrl || getTicketApiBase()).replace(/\/+$/, '')
  const url = `${base}/api/v1/viewer-worklists/${encodeURIComponent(id)}`
  const fetchFn = opts.fetchImpl || fetch

  let res
  try {
    res = await fetchFn(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })
  } catch (e) {
    return { ok: false, reason: 'network' }
  }

  if (res.status === 404 || res.status === 410) {
    return { ok: false, status: res.status, reason: 'invalid_worklist' }
  }
  if (!res.ok) {
    return { ok: false, status: res.status, reason: 'http' }
  }

  let data
  try {
    data = await res.json()
  } catch (e) {
    return { ok: false, status: res.status, reason: 'bad_json' }
  }

  const worklist = normalizeWorklistResponse(data)
  if (!worklist.items.length) {
    return { ok: false, status: res.status, reason: 'empty_worklist', worklist }
  }
  if (!worklist.worklistId) {
    worklist.worklistId = id
  }
  return { ok: true, status: res.status, worklist }
}

/**
 * @param {object} raw
 * @returns {{
 *   profile: string,
 *   shell: string|null,
 *   expiresAt: string|null,
 *   candidates: object[],
 *   patientId: string|null,
 *   relatedStudies: ReturnType<typeof normalizeRelatedStudies>,
 *   clientScope: 'lan'|'wan'|null,
 *   probe: ReturnType<typeof normalizeTicketProbe>,
 *   state?: string,
 *   raw: object,
 * }}
 */
export function normalizeTicketResponse(raw) {
  const data = raw && typeof raw === 'object' ? raw : {}
  const profile =
    parsePlanQueryParam(data.profile) ||
    parsePlanQueryParam(data.plan) ||
    UDV_PLANS.PATIENT
  const shell =
    data.shell != null && String(data.shell).trim() !== ''
      ? String(data.shell).trim()
      : null
  const candidates = Array.isArray(data.candidates) ? data.candidates.slice() : []
  const purpose =
    parseTicketPurposeParam(data.purpose) || TICKET_PURPOSE.PATIENT_SHARE
  const patientIdRaw =
    data.patient_id != null
      ? data.patient_id
      : data.patientId != null
        ? data.patientId
        : null
  const patientId =
    patientIdRaw != null && String(patientIdRaw).trim() !== ''
      ? String(patientIdRaw).trim()
      : null
  const relatedRaw =
    data.related_studies != null
      ? data.related_studies
      : data.relatedStudies
  return {
    profile,
    purpose,
    shell,
    theme: parseThemeQueryParam(data.theme),
    expiresAt: data.expires_at || data.expiresAt || null,
    candidates,
    patientId,
    relatedStudies: normalizeRelatedStudies(relatedRaw),
    state: data.state || null,
    edgeId: data.edge_id || data.edgeId || null,
    edgeLanBase: (function () {
      const raw = data.edge_lan_base != null ? data.edge_lan_base : data.edgeLanBase
      if (raw == null) return null
      const s = String(raw).trim().replace(/\/+$/, '')
      return s !== '' ? s : null
    })(),
    clientScope: (function () {
      const raw = data.client_scope != null ? data.client_scope : data.clientScope
      if (raw == null) return null
      const s = String(raw).trim().toLowerCase()
      return s === 'lan' || s === 'wan' ? s : null
    })(),
    probe: normalizeTicketProbe(data.probe),
    tenantId: data.tenant_id || data.tenantId || null,
    telemetryBaseUrl:
      data.telemetry_base_url || data.telemetryBaseUrl || null,
    telemetryToken:
      data.telemetry_token || data.telemetryToken || null,
    pwaInstall: parseBooleanPolicyField(
      data.pwa_install != null ? data.pwa_install : data.pwaInstall
    ),
    offlineCache: parseBooleanPolicyField(
      data.offline_cache != null ? data.offline_cache : data.offlineCache
    ),
    raw: data,
  }
}

/**
 * @param {unknown} raw
 * @returns {boolean}
 */
function parseBooleanPolicyField(raw) {
  if (raw == null || raw === '') return true
  if (typeof raw === 'boolean') return raw
  const v = String(raw).trim().toLowerCase()
  if (v === '0' || v === 'false' || v === 'no' || v === 'off') return false
  if (v === '1' || v === 'true' || v === 'yes' || v === 'on') return true
  return true
}

/**
 * @param {string} ticketId
 * @param {{ baseUrl?: string, fetchImpl?: typeof fetch }} [opts]
 * @returns {Promise<{
 *   ok: boolean,
 *   status?: number,
 *   ticket?: ReturnType<typeof normalizeTicketResponse>,
 *   reason?: string,
 * }>}
 */
export async function resolveViewerTicket(ticketId, opts = {}) {
  const id = ticketId != null ? String(ticketId).trim() : ''
  if (!id) {
    return { ok: false, reason: 'missing_ticket' }
  }
  const base = (opts.baseUrl || getTicketApiBase()).replace(/\/+$/, '')
  const url = `${base}/api/v1/viewer-tickets/${encodeURIComponent(id)}`
  const fetchFn = opts.fetchImpl || fetch

  let res
  try {
    res = await fetchFn(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })
  } catch (e) {
    return { ok: false, reason: 'network' }
  }

  if (res.status === 404 || res.status === 410) {
    return { ok: false, status: res.status, reason: 'invalid_ticket' }
  }
  if (res.status === 409) {
    let state = 'ingesting'
    try {
      const body = await res.json()
      if (body && body.state) state = String(body.state)
    } catch (e) {
      /* ignore */
    }
    return { ok: false, status: 409, reason: 'ticket_pending', state }
  }
  if (!res.ok) {
    return { ok: false, status: res.status, reason: 'http' }
  }

  let data
  try {
    data = await res.json()
  } catch (e) {
    return { ok: false, status: res.status, reason: 'bad_json' }
  }

  const ticket = normalizeTicketResponse(data)
  if (!ticket.candidates.length) {
    return { ok: false, status: res.status, reason: 'no_candidates', ticket }
  }
  return { ok: true, status: res.status, ticket }
}

/**
 * Canje JWT elev (POST udv-elevation). CONTRATO-UDV-ELEVATION.md
 * @param {string} ticketId
 * @param {{ baseUrl?: string, elevationToken?: string, fetchImpl?: typeof fetch }} [opts]
 * @returns {Promise<{
 *   ok: boolean,
 *   authorized?: boolean,
 *   plan?: string|null,
 *   shell?: string|null,
 *   edgeId?: string|null,
 *   purpose?: string|null,
 *   reason?: string|null,
 *   status?: number,
 * }>}
 */
export async function fetchUdvElevation(ticketId, opts = {}) {
  const id = ticketId != null ? String(ticketId).trim() : ''
  const token =
    opts.elevationToken != null ? String(opts.elevationToken).trim() : ''
  if (!id || !token) {
    return { ok: false, authorized: false, reason: 'missing_token' }
  }
  const base = (opts.baseUrl || getTicketApiBase()).replace(/\/+$/, '')
  const url = `${base}/api/v1/viewer-tickets/${encodeURIComponent(id)}/udv-elevation`
  const fetchFn = opts.fetchImpl || fetch

  let res
  try {
    res = await fetchFn(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ elevation_token: token }),
    })
  } catch (e) {
    return { ok: false, authorized: false, reason: 'network' }
  }

  let data = null
  try {
    data = await res.json()
  } catch (e) {
    /* ignore */
  }

  if (res.status === 403) {
    return {
      ok: true,
      authorized: false,
      reason: (data && data.reason) || 'patient_share_only',
      status: res.status,
    }
  }

  if (!res.ok) {
    return {
      ok: false,
      authorized: false,
      reason: (data && data.reason) || 'http',
      status: res.status,
    }
  }

  if (!data || data.authorized !== true) {
    return {
      ok: true,
      authorized: false,
      reason: (data && data.reason) || 'no_session',
      status: res.status,
    }
  }

  const leaseTokenRaw =
    data.lease_token != null
      ? data.lease_token
      : data.leaseToken != null
        ? data.leaseToken
        : null
  const leaseToken =
    leaseTokenRaw != null && String(leaseTokenRaw).trim() !== ''
      ? String(leaseTokenRaw).trim()
      : null
  const leaseExpiresAtRaw =
    data.lease_expires_at != null
      ? data.lease_expires_at
      : data.leaseExpiresAt != null
        ? data.leaseExpiresAt
        : null
  const leaseExpiresAt =
    leaseExpiresAtRaw != null && String(leaseExpiresAtRaw).trim() !== ''
      ? String(leaseExpiresAtRaw).trim()
      : null
  const intervalRaw =
    data.extend_interval_seconds != null
      ? data.extend_interval_seconds
      : data.extendIntervalSeconds
  const intervalN = Number(intervalRaw)
  const extendIntervalSeconds =
    Number.isFinite(intervalN) && intervalN >= 30
      ? Math.floor(intervalN)
      : null

  return {
    ok: true,
    authorized: true,
    plan: data.plan != null ? String(data.plan) : null,
    shell: data.shell != null ? String(data.shell) : null,
    theme: parseThemeQueryParam(data.theme),
    edgeId: data.edge_id || data.edgeId || null,
    purpose: data.purpose != null ? String(data.purpose) : null,
    leaseToken,
    leaseExpiresAt,
    extendIntervalSeconds,
    status: res.status,
  }
}

/**
 * Quita `elev` de la barra tras canjear (share-safe).
 */
export function stripElevationFromUrl() {
  if (typeof window === 'undefined' || !window.history || !window.location) {
    return
  }
  try {
    const url = new URL(window.location.href)
    if (!url.searchParams.has('elev')) return
    url.searchParams.delete('elev')
    const qs = url.searchParams.toString()
    const next = url.pathname + (qs ? `?${qs}` : '') + url.hash
    window.history.replaceState({}, '', next)
  } catch (e) {
    /* ignore */
  }
}
